import { useFtpeMasque, estSuperAdmin } from '../../components/FtpeMasqueToggle';
import { useProrataConfig, calculerProrata, appliquerProrata } from './prorataAvenant';
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calculator, AlertCircle, CheckCircle, AlertTriangle, Info, Search, Loader2, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { showFormErrorFeedback } from '../../lib/formFeedback';
import { useAuth } from '../../contexts/AuthContext';
import { useFormValidation } from '../../components/ui/FormField';
import { computeTtcFromHtAndTaxes, normalizeTaxesSource } from '../../lib/computeTtc';
import { useOfflineStatus } from '../../components/OfflineStatusIndicator';
import { API } from './constants';
import FormulaireSection from './FormulaireSection';
import ResultatsSection from './ResultatsSection';
import HeaderSection from './HeaderSection';
import EligibilityProfilePanel from './EligibilityProfilePanel';
import { EditContactModal } from '../../components/MembresTable/modals';
import { useOfflineMode } from './useOfflineMode';
import { useContratHandlers } from './useContratHandlers';
import { getToken } from '../../services/tokenService';
import { useCalculationLogic } from './useCalculationLogic';
import { useBaticoEffects } from './useBaticoEffects';
import { useSouscriptionActions } from './useSouscriptionActions';
import { useDevisDownload } from './useDevisDownload';

export function useCalculateurBatico() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // === Mode hors ligne ===
  const { isOnline, unsyncedCount } = useOfflineStatus();
  
  // État pour le suivi des champs touchés (validation)
  const [touched, setTouched] = useState({});
  
  // Mode principal: 'souscription' ou 'avenant'
  const [modeSimulateur, setModeSimulateur] = useState('souscription');

  // Détection d'un retour redirigé depuis le workflow de souscription
  // (cf. /member/souscription qui renvoie vers /calculateur-batico?from=souscription)
  const fromSouscription = new URLSearchParams(location.search).get('from') === 'souscription';
  
  // === États pour le système de crédits ESS ===
  const [walletBalance, setWalletBalance] = useState(null);
  const [creditCostPdf, setCreditCostPdf] = useState(2); // Coût par défaut pour génération PDF
  
  // Les admins et super_admins n'ont pas besoin de crédits
  const isAdminOrSuperAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const hasEnoughCredits = isAdminOrSuperAdmin || walletBalance === null || walletBalance >= creditCostPdf;
  
  // Déterminer le dashboard de retour selon le rôle de l'utilisateur
  const getDashboardPath = () => {
    if (!user) return '/';
    const role = user.role?.toLowerCase();
    if (role === 'super_admin' || role === 'superadmin') return '/admin/super-dashboard';
    if (role === 'admin') return '/admin/dashboard';
    if (role === 'cooper' || role === 'coopers') return '/cooper/dashboard';
    if (role === 'expert') return '/expert/dashboard';
    if (role === 'member' || role === 'membre') return '/member/dashboard';
    return '/';
  };

  const getDashboardLabel = () => {
    if (!user) return 'Accueil';
    const role = user.role?.toLowerCase();
    if (role === 'super_admin' || role === 'superadmin') return 'Super Admin';
    if (role === 'admin') return 'Admin';
    if (role === 'cooper' || role === 'coopers') return "COOPER'S";
    if (role === 'expert') return "COOPER'S Advisor";
    if (role === 'member' || role === 'membre') return 'Membre';
    return 'Accueil';
  };

  const [formData, setFormData] = useState({
    ca_ht: '',
    nombre_jours: ''
  });
  
  // Champs spécifiques pour souscriptions nominatives
  const [operationNominative, setOperationNominative] = useState({
    type_operation: '',  // construction_neuve, rehabilitation, extension
    adresse_operation: '',
    code_postal_operation: '',
    ville_operation: '',
    pays_operation: 'France',
    description_operation: '',  // Description de l'opération
    territoire_intervention: ''  // DOM, COM, Métropole
  });
  
  // Options pour les types d'opération et territoires (chargées depuis l'API)
  const [typesOperation, setTypesOperation] = useState([
    { value: 'construction_neuve', label: 'Construction neuve' },
    { value: 'rehabilitation', label: 'Réhabilitation' },
    { value: 'extension', label: 'Extension / Modification' }
  ]);
  
  const [territoiresIntervention, setTerritoiresIntervention] = useState([
    { value: 'metropole', label: 'Métropole' },
    { value: 'dom', label: 'DOM (Départements d\'Outre-Mer)' },
    { value: 'com', label: 'COM (Collectivités d\'Outre-Mer)' }
  ]);
  
  // Charger les options configurables depuis l'API
  
  // Charger le wallet de l'utilisateur connecté
  
  // Données du membre pré-remplies (depuis URL params)
  const [membreData, setMembreData] = useState(null);

  // V38.01 — Modale « Compléter le profil » (depuis le bandeau des champs
  // absents du profil membre). À la sauvegarde, le membre mis à jour est
  // fusionné dans membreData → le préremplissage se ré-arme et complète
  // les champs encore vides du simulateur.
  const [showProfilModal, setShowProfilModal] = useState(false);
  const handleProfilSaved = (updated) => {
    if (updated) setMembreData(prev => ({ ...prev, ...updated }));
  };

  // Conversion proposition → souscription (bannière post-téléchargement)
  // { devisId, validiteJours, dateValidite, dateValiditeISO } | null
  const [propositionConversion, setPropositionConversion] = useState(null);
  
  // Mode reprise de simulation existante
  const [resumeMode, setResumeMode] = useState(false);
  const [resumeSimulationId, setResumeSimulationId] = useState(null);

  // Versioning : si on reprend une proposition existante (via `?proposition&from=batico_resume`),
  // on enregistre l'ID parent pour que la prochaine génération PDF soit liée à la même lignée
  // (v1 → v2 → v3 dans la collection `batico_propositions`).
  const [resumeParentDevisId, setResumeParentDevisId] = useState(null);
  // V39.54 — Reprise depuis une souscription de l'espace membre : marque la
  // prochaine proposition générée comme « Reprise simulateur » (badge Mes Documents).
  const [repriseSimulateur, setRepriseSimulateur] = useState(false);
  
  // Charger les données de pré-remplissage depuis l'URL (prefill OU resume)

  // Pré-chargement du membre cible pour un admin/cooper qui ouvre le
  // simulateur depuis le tableau des membres avec `?membre_id=…` ou
  // `?membre=…`. Sans cela, EligibilityProfilePanel reçoit l'utilisateur
  // connecté (admin) en prop `membre`, et l'auto-fill simulator-aware ne se
  // déclenche jamais (l'admin n'a pas les documents du membre cible).
  //
  // V35.58 — Aussi déclencher le fetch quand `membreData` est déjà rempli
  // par les URL params (mode `nouveau=true`) mais qu'il manque les champs
  // RH nécessaires au prefill (effectif / date_creation_entreprise /
  // experience_professionnelle_annees). On fusionne ensuite les données
  // serveur dans `membreData` (les URL params restent une source pour
  // raison_sociale/siret/etc, et la DB enrichit les RH).

  // Restauration depuis email Brevo : ?proposition=<devisId>&token=<...>
  // OU restauration authentifiée depuis le tableau membres :
  //   ?proposition=<devisId>&from=batico_resume[&membre=<id>]
  // Récupère le snapshot persisté côté backend pour rouvrir la bannière de
  // conversion + restaurer les éléments visuels essentiels (CA, mode, jours).
  // Le token signé est validé par le backend (résistant aux brute force /
  // expiration). Tolère silencieusement un token expiré : on n'affiche pas
  // d'erreur bloquante, juste un toast informatif.
  
  // Période de validité personnalisée (Super Admin uniquement)
  const [validitePersonnalisee, setValiditePersonnalisee] = useState({
    enabled: false,
    date_debut: '',
    date_fin: '',
    validite_jours: 365
  });
  
  // Montants personnalisés (Super Admin uniquement)
  const [montantsPersonnalises, setMontantsPersonnalises] = useState({
    enabled: false,
    ca_personnalise: '',
    montant_ht_personnalise: '',
    montant_ttc_personnalise: '',
    remise_pourcentage: '',
    remise_euros: ''
  });
  
  // Taxes et contributions personnalisées (Super Admin uniquement)
  // Chargées dynamiquement depuis Config BATICO avancée selon le type RCDS
  const [taxesPersonnalisees, setTaxesPersonnalisees] = useState({
    enabled: false,
    lignes: []
  });
  
  // NOTE: Le useEffect pour charger les taxes est défini plus bas, après modeCalcul
  
  // Ajouter une nouvelle ligne de taxe/contribution
  
  // Supprimer une ligne de taxe/contribution
  
  // Mettre à jour une ligne de taxe
  
  // Calculer les taxes finales
  
  // Vérifier si l'utilisateur est Super Admin
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'superadmin';
  
  // Calculer les montants finaux (avec personnalisation si activée)
  
  // Nouveau système : sélection d'activités par ID
  const [selectedActivitesGO, setSelectedActivitesGO] = useState([]);
  const [selectedActivites2nd, setSelectedActivites2nd] = useState([]);
  const [selectedActivitesPIB, setSelectedActivitesPIB] = useState([]);
  const [selectedActivitesTP, setSelectedActivitesTP] = useState([]);
  
  // Stocker les activités complètes pour le PDF (avec définition et exclusions)
  const [activitesCompletes, setActivitesCompletes] = useState({
    gros_oeuvre: [],
    second_oeuvre: [],
    travaux_publics: [],
    pib: []
  });
  
  // Callback pour récupérer les activités complètes chargées par SelecteurActivites
  
  // Fonction pour récupérer les activités sélectionnées avec leurs détails complets
  
  const [resultat, setResultat] = useState(null);
  // Profil d'éligibilité (modulateurs avancés)
  const [eligibilityForm, setEligibilityForm] = useState({});
  const [eligibilityValid, setEligibilityValid] = useState(true);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // V36.62 — Override Super Admin (force validation + taux personnalisé)
  const [forceValidationTaux, setForceValidationTaux] = useState('1.30');
  const [forceValidationLoading, setForceValidationLoading] = useState(false);

  // Sync automatique : le total du CA par territoire (panneau d'éligibilité)
  // alimente le champ "Chiffre d'affaires *" de la section Paramètres de calcul.
  // L'utilisateur peut toujours surcharger manuellement, mais toute modification
  // ultérieure d'une tranche territoriale réécrit le total.

  const [loadingDevis, setLoadingDevis] = useState(false);
  // V36.73 — État de l'échéancier actuel (par défaut OU personnalisé Super Admin)
  // Mis à jour par <EchelonnementPreview onPlanChange={...}/> à chaque calcul/preview.
  // Sert à transmettre l'override au backend lors de "Télécharger la Proposition ESS PDF".
  const [currentEcheancierPlan, setCurrentEcheancierPlan] = useState(null);
  const [modeCalcul, setModeCalcul] = useState('annuel');
  const [isFtpeMemberReel, setIsFtpeMember] = useState(false);
  const [ftpeMasque] = useFtpeMasque();

  // === Custom hooks ===
  const {
    localSimulations, setLocalSimulations,
    showLocalSimulations, setShowLocalSimulations,
    saveToLocal,
    handleLoadLocalSimulation,
    handleDeleteLocalSimulation
  } = useOfflineMode({
    modeCalcul,
    userId: user?.id,
    setters: {
      setFormData, setSelectedActivitesGO, setSelectedActivites2nd,
      setSelectedActivitesPIB, setSelectedActivitesTP, setModeCalcul, setResultat
    }
  });

  const {
    referenceRecherche, setReferenceRecherche,
    contratParent, setContratParent,
    loadingContrat,
    contratsDisponibles, loadingContrats,
    erreurContrat, setErreurContrat,
    handleRechercherContrat,
    handleSelectContrat,
    handleProcederAuPaiement,
    handleResetAvenant: handleResetAvenantBase
  } = useContratHandlers({
    user, modeSimulateur, navigate, setLoading,
    resetForm: () => {
      setResultat(null);
      setSelectedActivitesGO([]);
      setSelectedActivites2nd([]);
      setSelectedActivitesPIB([]);
      setSelectedActivitesTP([]);
      setFormData({ ca_ht: '', nombre_jours: '' });
      // Réautoriser un calcul auto après reset avenant
      if (autoCalcDoneRef.current !== undefined) autoCalcDoneRef.current = false;
    }
  });

  const handleResetAvenant = handleResetAvenantBase;

  // Vérifier le statut FTPE du membre/utilisateur

  // Remise solidaire négociée (pré-paramétrée au niveau du membre).
  // Si configurée, on active automatiquement le panneau « Personnalisation des
  // montants » et on pré-remplit la remise % et/ou € à la valeur autorisée.
  const [remiseSolidaireNegociee, setRemiseSolidaireNegociee] = useState(null);
  const remiseAutoAppliedRef = useRef(false);
  const [restrictions, setRestrictions] = useState([]);
  const [loadingRestrictions, setLoadingRestrictions] = useState(true);
  
  // Charger les taxes configurées selon le type (annuel/nominatif)
  
  // Paramètres BATICO (seuils CA, coefficients) chargés depuis la configuration avancée
  const [parametresBatico, setParametresBatico] = useState({
    seuil_ca: 40000,
    seuil_ca_actif: false,
    coef_gros_oeuvre: 0.002945,
    coef_second_oeuvre: 0.002695,
    coef_intellectuels: 0.0045,
    coef_travaux_publics: 0.00425,
    // Seuils par type (annuel/nominatif)
    seuils_ca_par_type: {
      annuel: { seuil_ca: 40000, seuil_ca_actif: true, message: '' },
      nominatif: { seuil_ca: 5000, seuil_ca_actif: true, message: '' }
    },
    coefficients_par_type: {},
    remises_par_type: {},
    taxes_par_type: {}
  });
  
  // Obtenir le seuil CA actif selon le mode de calcul
  const getSeuilCAActif = useCallback(() => {
    const typeMode = modeCalcul === 'nominative' ? 'nominatif' : 'annuel';
    const seuilConfig = parametresBatico.seuils_ca_par_type?.[typeMode];
    return {
      seuil: seuilConfig?.seuil_ca || parametresBatico.seuil_ca || 40000,
      actif: seuilConfig?.seuil_ca_actif ?? parametresBatico.seuil_ca_actif ?? false,
      message: seuilConfig?.message || ''
    };
  }, [modeCalcul, parametresBatico]);
  
  // Validation inline des champs
  const { errors, setError, clearError, validateForm, clearAllErrors } = useFormValidation();
  
  // Détecter le mode avenant depuis les paramètres URL

  // Charger les paramètres BATICO actifs (seuils CA, coefficients)

  // Charger les restrictions BATICO au démarrage
  
  // Pré-remplir le CA quand un contrat parent est sélectionné
  
  // Déterminer l'accès selon le rôle
  const canAccessAnnuel = user && (user.role === 'cooper' || user.role === 'super_admin' || user.role === 'admin');
  const canAccessNominative = user && (user.role === 'super_admin' || user.role === 'admin');



  // ============================================================
  // CALCUL AUTO UNIQUE — Lance automatiquement handleCalculer()
  // la PREMIÈRE fois que toutes les conditions minimales sont
  // réunies (CA > 0 + au moins 1 activité + profil d'éligibilité
  // minimal renseigné). Une fois lancé, ne se redéclenche plus,
  // l'utilisateur reprend la main avec le bouton "Calculer".
  //
  // Réinitialisé par handleResetAvenant() et handleResetSimulation()
  // pour permettre un nouveau cycle auto sur une nouvelle simulation.
  // ============================================================
  const autoCalcDoneRef = useRef(false);
  const autoCalcTimerRef = useRef(null);
  // Effets d'init / prefill / restore extraits (ctx-bag)
  useBaticoEffects({
    contratParent, eligibilityForm, location, membreData, modeCalcul, remiseAutoAppliedRef,
    restrictions, setFormData, setIsFtpeMember, setLoadingRestrictions, setMembreData, setModeCalcul,
    setModeSimulateur, setMontantsPersonnalises, setOperationNominative, setParametresBatico, setPropositionConversion, setRemiseSolidaireNegociee,
    setRestrictions, setResumeMode, setResumeParentDevisId, setResumeSimulationId, setSelectedActivites2nd, setSelectedActivitesGO,
    setSelectedActivitesPIB, setSelectedActivitesTP, setTaxesPersonnalisees, setTerritoiresIntervention, setTypesOperation, setWalletBalance,
    setRepriseSimulateur, setValiditePersonnalisee, setContratParent,
    autoCalcDoneRef, setResultat,
    user,
  });

  // Logique de calcul (ctx-bag) — useCalculationLogic
  const {
    ajouterLigneTaxe, supprimerLigneTaxe, updateLigneTaxe, getTaxesFinals, getMontantsFinals,
    handleActivitesLoaded, getActivitesSelectionneesFull, handleCalculer, handleReset,
  } = useCalculationLogic({
      activitesCompletes, autoCalcDoneRef, canAccessAnnuel, canAccessNominative, clearAllErrors, eligibilityForm,
      eligibilityResult, eligibilityValid, formData, getSeuilCAActif, isOnline, isSuperAdmin,
      modeCalcul, montantsPersonnalises, resultat, saveToLocal, selectedActivites2nd, selectedActivitesGO,
      selectedActivitesPIB, selectedActivitesTP, setActivitesCompletes, setEligibilityResult, setError, setFormData,
      setLoading, setPropositionConversion, setResultat, setTaxesPersonnalisees, taxesPersonnalisees,
  });

  // Actions de souscription (ctx-bag) — useSouscriptionActions
  const {
    handleCreerAvenant, handleSaveForLater, handleSouscrire, handleSouscrireFromProposition,
  } = useSouscriptionActions({
      contratParent, eligibilityForm, formData, getActivitesSelectionneesFull, getMontantsFinals, getTaxesFinals,
      isSuperAdmin, location, membreData, modeCalcul, navigate, operationNominative,
      propositionConversion, resultat, resumeParentDevisId, selectedActivites2nd, selectedActivitesGO, selectedActivitesPIB,
      selectedActivitesTP, setLoading, setPropositionConversion, user, validitePersonnalisee,
  });

  const isFtpeMember = isFtpeMemberReel || (estSuperAdmin(user) && ftpeMasque);
  // Téléchargement devis/proposition (ctx-bag) — useDevisDownload
  const {
    handleDownloadDevisSimulation,
  } = useDevisDownload({
      contratParent, creditCostPdf, currentEcheancierPlan, formData, getActivitesSelectionneesFull, getMontantsFinals,
      getTaxesFinals, handleSouscrireFromProposition, isAdminOrSuperAdmin, isFtpeMember, isSuperAdmin, location,
      membreData, modeCalcul, operationNominative, resultat, resumeParentDevisId, selectedActivites2nd,
      selectedActivitesGO, selectedActivitesPIB, selectedActivitesTP, setLoadingDevis, setPropositionConversion, setResumeParentDevisId,
      setWalletBalance, validitePersonnalisee, walletBalance, repriseSimulateur,
  });

  useEffect(() => {
    if (autoCalcDoneRef.current) return;
    if (resultat || loading || !isOnline) return;

    const totalActivites =
      (selectedActivitesGO?.length || 0) +
      (selectedActivites2nd?.length || 0) +
      (selectedActivitesPIB?.length || 0) +
      (selectedActivitesTP?.length || 0);
    if (totalActivites === 0) return;

    const ca = parseFloat(formData.ca_ht);
    if (!ca || ca <= 0) return;

    // Profil d'éligibilité minimal renseigné (anciennete + experience + effectif + type_marche)
    const ef = eligibilityForm || {};
    const minEligibilityOk =
      ef.anciennete_annees !== undefined && ef.anciennete_annees !== '' && ef.anciennete_annees !== null &&
      ef.experience_professionnelle_annees !== undefined && ef.experience_professionnelle_annees !== '' && ef.experience_professionnelle_annees !== null &&
      ef.effectif &&
      ef.type_marche;
    if (!minEligibilityOk) return;

    // Validation côté nominative : nombre de jours requis
    if (modeCalcul === 'nominative') {
      const nj = parseInt(formData.nombre_jours);
      if (!nj || nj <= 0 || nj > 360) return;
    }

    // Debounce 600ms pour absorber les saisies en cours
    if (autoCalcTimerRef.current) clearTimeout(autoCalcTimerRef.current);
    autoCalcTimerRef.current = setTimeout(() => {
      autoCalcDoneRef.current = true;
      toast.info('Calcul automatique en cours…', { duration: 1500 });
      handleCalculer();
    }, 600);

    return () => {
      if (autoCalcTimerRef.current) clearTimeout(autoCalcTimerRef.current);
    };
    // handleCalculer est volontairement exclu pour éviter de recréer le timer à chaque rendu.
  }, [
    formData.ca_ht, formData.nombre_jours, modeCalcul,
    selectedActivitesGO, selectedActivites2nd, selectedActivitesPIB, selectedActivitesTP,
    eligibilityForm, isOnline, resultat, loading,
  ]);

  // Bannière de conversion → souscription en 1 clic
  // Auto-détecte le rôle utilisateur pour choisir le workflow cible.

  // Bannière "Plus tard" — persiste la proposition côté backend (idempotent)
  // pour qu'elle apparaisse dans le tableau "Mes propositions" du membre /
  // cooper, puis ferme la bannière avec un toast de confirmation.
  // Avant cette V35.54, "Plus tard" se contentait de masquer la bannière sans
  // sauvegarder explicitement, ce qui rendait la proposition irréutilisable
  // si l'utilisateur n'avait pas téléchargé le PDF.

  
  // Créer un avenant

  // Générer et télécharger la proposition ESS de simulation (avant souscription)


  // Avenant : prorata automatique (mois restants ÷ 12) jusqu'à la fin du contrat parent
  const [prorataConfig] = useProrataConfig();
  useEffect(() => {
    if (!resultat || resultat.montant_ht == null) return;
    const brut = resultat._brut || resultat;
    if (modeSimulateur !== 'avenant' || !contratParent) {
      if (resultat._brut) setResultat(resultat._brut);
      return;
    }
    const p = calculerProrata(prorataConfig, contratParent.date_fin || contratParent.periode_adhesion_fin);
    const cur = resultat.prorata;
    if (resultat._brut && cur && Math.abs(cur.facteur - p.facteur) < 0.00005 && cur.mode === p.mode) return;
    setResultat(appliquerProrata(brut, p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultat, modeSimulateur, contratParent, prorataConfig]);

  // ctx-bag : état + handlers + retours de hooks partagés avec la vue extraite
  const ctx = {
    activitesCompletes, creditCostPdf, currentEcheancierPlan, eligibilityForm, eligibilityResult,
    eligibilityValid, forceValidationLoading, forceValidationTaux, formData, isFtpeMember, loading,
    loadingDevis, loadingRestrictions, membreData, modeCalcul, modeSimulateur, montantsPersonnalises,
    operationNominative, parametresBatico, propositionConversion, remiseSolidaireNegociee, restrictions,
    resultat, resumeMode, resumeParentDevisId, resumeSimulationId, selectedActivites2nd, selectedActivitesGO,
    selectedActivitesPIB, selectedActivitesTP, showProfilModal, taxesPersonnalisees, territoiresIntervention,
    touched, typesOperation, validitePersonnalisee, walletBalance,
    setActivitesCompletes, setCreditCostPdf, setCurrentEcheancierPlan, setEligibilityForm, setEligibilityResult,
    setEligibilityValid, setForceValidationLoading, setForceValidationTaux, setFormData, setIsFtpeMember,
    setLoading, setLoadingDevis, setLoadingRestrictions, setMembreData, setModeCalcul, setModeSimulateur,
    setMontantsPersonnalises, setOperationNominative, setParametresBatico, setPropositionConversion,
    setRemiseSolidaireNegociee, setRestrictions, setResultat, setResumeMode, setResumeParentDevisId,
    setResumeSimulationId, setSelectedActivites2nd, setSelectedActivitesGO, setSelectedActivitesPIB,
    setSelectedActivitesTP, setShowProfilModal, setTaxesPersonnalisees, setTerritoiresIntervention,
    setTouched, setTypesOperation, setValiditePersonnalisee, setWalletBalance,
    user, isOnline, unsyncedCount, navigate, location,
    localSimulations, setLocalSimulations, showLocalSimulations, setShowLocalSimulations, saveToLocal,
    handleLoadLocalSimulation, handleDeleteLocalSimulation,
    referenceRecherche, setReferenceRecherche, contratParent, setContratParent, loadingContrat,
    contratsDisponibles, loadingContrats, erreurContrat, setErreurContrat, handleRechercherContrat,
    handleSelectContrat, handleProcederAuPaiement, handleResetAvenant,
    errors, setError, clearError, validateForm, clearAllErrors,
    ajouterLigneTaxe, canAccessAnnuel, canAccessNominative, fromSouscription, getActivitesSelectionneesFull,
    getDashboardLabel, getDashboardPath, getMontantsFinals, getSeuilCAActif, getTaxesFinals,
    handleActivitesLoaded, handleCalculer, handleCreerAvenant, handleDownloadDevisSimulation, handleProfilSaved,
    handleReset, handleSaveForLater, handleSouscrire, handleSouscrireFromProposition, hasEnoughCredits,
    isAdminOrSuperAdmin, isSuperAdmin, supprimerLigneTaxe, updateLigneTaxe,
  };

  return ctx;
}

export default useCalculateurBatico;
